// Phase 2 evaluator types are TypeScript-only; JSON validation remains in crosswalk-schema.ts.
export type FoodCategoryPolicyRuleMode = "direct" | "fallback" | "mixed" | "branch-to-descendant";

export type FoodCategoryPolicyRule = {
  sourceId: string;
  mode: FoodCategoryPolicyRuleMode;
  categoryKey?: string;
  fallbackCategoryKey?: string;
  preferredCategoryKeys?: string[];
  blockedByCategoryKeys?: string[];
  requiresSupport?: string[];
};

export type FoodCategoryPolicyTextRefinementRule = {
  sourceId?: string;
  sourceIds?: readonly string[];
  matchWhenOnlySourceIds?: readonly string[];
  matchWhenNoSourceIds?: boolean;
  categoryKey: string;
  reason: string;
  phrases?: readonly string[];
  allTokens?: readonly string[];
  anyTokens?: readonly string[];
  noneTokens?: readonly string[];
};

export type FoodCategoryPolicyGraph = {
  getAncestorSourceIds?: (sourceId: string) => readonly string[];
  isDescendantOf?: (args: {
    descendantCategoryKey: string;
    ancestorCategoryKey: string;
  }) => boolean;
};

export type FoodCategoryPolicyEvaluation = {
  inputSourceIds: string[];
  ignoredSourceIds: string[];
  unmatchedSourceIds: string[];
  highRiskOverrideSourceIds: string[];
  directCategoryKeys: string[];
  inferredPreferredCategoryKeys: string[];
  fallbackCategoryKeys: string[];
  blockedFallbacks: {
    sourceId: string;
    categoryKey: string;
    blockedByCategoryKeys: string[];
  }[];
  mixedCandidates: {
    sourceId: string;
    preferredCategoryKeys: string[];
    fallbackCategoryKey: string | null;
    requiresSupport: string[];
  }[];
  textRefinement: {
    sourceId: string | null;
    categoryKey: string;
    reason: string;
  } | null;
  orderedCategoryKeys: string[];
};

function unique(values: readonly string[]) {
  return [...new Set(values)];
}

function canInferPreferredCategory(rule: FoodCategoryPolicyRule, graph?: FoodCategoryPolicyGraph) {
  if (
    rule.mode === "branch-to-descendant" &&
    rule.preferredCategoryKeys?.length === 1 &&
    !rule.requiresSupport?.length
  ) {
    const preferredCategoryKey = rule.preferredCategoryKeys[0] ?? null;
    const ancestorCategoryKey = rule.fallbackCategoryKey ?? rule.categoryKey;
    if (!preferredCategoryKey || !ancestorCategoryKey || !graph?.isDescendantOf) {
      return null;
    }

    return graph.isDescendantOf({
      descendantCategoryKey: preferredCategoryKey,
      ancestorCategoryKey,
    })
      ? preferredCategoryKey
      : null;
  }

  return null;
}

function tokenize(text: string) {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

function escapeRegExp(text: string) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function includesPhrase(normalizedText: string, phrase: string) {
  const normalizedPhrase = phrase.toLowerCase();
  return new RegExp(`(^|[^a-z0-9])${escapeRegExp(normalizedPhrase)}($|[^a-z0-9])`).test(
    normalizedText,
  );
}

function matchesTextRefinementRule(rule: FoodCategoryPolicyTextRefinementRule, text: string) {
  const normalizedText = text.toLowerCase();
  const tokens = tokenize(text);

  if (
    rule.phrases?.length &&
    !rule.phrases.some((phrase) => includesPhrase(normalizedText, phrase))
  ) {
    return false;
  }

  if (rule.allTokens?.length && !rule.allTokens.every((token) => tokens.includes(token))) {
    return false;
  }

  if (rule.anyTokens?.length && !rule.anyTokens.some((token) => tokens.includes(token))) {
    return false;
  }

  if (rule.noneTokens?.length && rule.noneTokens.some((token) => tokens.includes(token))) {
    return false;
  }

  return (
    Boolean(rule.phrases?.length || rule.allTokens?.length || rule.anyTokens?.length) ||
    !rule.noneTokens?.length
  );
}

function expandSourceIds(sourceIds: readonly string[], graph?: FoodCategoryPolicyGraph) {
  const directSourceIds = unique(sourceIds.filter((sourceId) => sourceId.length > 0));
  if (!graph?.getAncestorSourceIds) {
    return directSourceIds;
  }

  const ancestorSourceIds = directSourceIds.flatMap(
    (sourceId) => graph.getAncestorSourceIds?.(sourceId) ?? [],
  );
  return unique([
    ...directSourceIds,
    ...ancestorSourceIds.filter((sourceId) => sourceId.length > 0),
  ]);
}

function ruleSourceIds(rule: FoodCategoryPolicyTextRefinementRule) {
  return unique([...(rule.sourceId ? [rule.sourceId] : []), ...(rule.sourceIds ?? [])]);
}

function matchesTextRefinementSourceIds(
  rule: FoodCategoryPolicyTextRefinementRule,
  inputSourceIds: readonly string[],
) {
  const sourceIds = ruleSourceIds(rule);
  const hasSourceMatch =
    sourceIds.length === 0 || sourceIds.some((sourceId) => inputSourceIds.includes(sourceId));
  if (!hasSourceMatch) {
    return false;
  }

  if (rule.matchWhenNoSourceIds && inputSourceIds.length === 0) {
    return true;
  }

  if (rule.matchWhenOnlySourceIds) {
    const allowedSourceIds = new Set(rule.matchWhenOnlySourceIds);
    if (inputSourceIds.every((sourceId) => allowedSourceIds.has(sourceId))) {
      return true;
    }
  }

  return sourceIds.length > 0;
}

type FoodCategoryPolicyEvaluationArgs = {
  sourceIds: readonly string[];
  ignoreSourceIds?: readonly string[];
  highRiskOverrideSourceIds?: readonly string[];
  rules: readonly FoodCategoryPolicyRule[];
  textInputs?: readonly (string | null | undefined)[];
  textRefinementRules?: readonly FoodCategoryPolicyTextRefinementRule[];
  graph?: FoodCategoryPolicyGraph;
};

function evaluateFoodCategoryPolicyInternal(
  args: FoodCategoryPolicyEvaluationArgs,
): FoodCategoryPolicyEvaluation {
  const inputSourceIds = expandSourceIds(args.sourceIds, args.graph);
  const ignoreSourceIdSet = new Set(args.ignoreSourceIds ?? []);
  const highRiskSourceIdSet = new Set(args.highRiskOverrideSourceIds ?? []);
  const rulesBySourceId = new Map(args.rules.map((rule) => [rule.sourceId, rule]));

  const ignoredSourceIds: string[] = [];
  const unmatchedSourceIds: string[] = [];
  const highRiskOverrideSourceIds: string[] = [];
  const directCategoryKeys: string[] = [];
  const inferredPreferredCategoryKeys: string[] = [];
  const fallbackCategoryKeys: string[] = [];
  const blockedFallbacks: FoodCategoryPolicyEvaluation["blockedFallbacks"] = [];
  const mixedCandidates: FoodCategoryPolicyEvaluation["mixedCandidates"] = [];
  const matchedRules: Array<{ sourceId: string; rule: FoodCategoryPolicyRule }> = [];
  const refinementText = (args.textInputs ?? []).filter(Boolean).join(" ");

  for (const sourceId of inputSourceIds) {
    if (ignoreSourceIdSet.has(sourceId)) {
      ignoredSourceIds.push(sourceId);
      continue;
    }

    const rule = rulesBySourceId.get(sourceId);
    if (!rule) {
      unmatchedSourceIds.push(sourceId);
      continue;
    }

    if (highRiskSourceIdSet.has(sourceId)) {
      highRiskOverrideSourceIds.push(sourceId);
    }

    matchedRules.push({ sourceId, rule });

    if (rule.mode === "direct" && rule.categoryKey) {
      directCategoryKeys.push(rule.categoryKey);
      continue;
    }

    if ((rule.mode === "mixed" || rule.mode === "branch-to-descendant") && rule.categoryKey) {
      inferredPreferredCategoryKeys.push(rule.categoryKey);
    }

    const inferredPreferred = canInferPreferredCategory(rule, args.graph);
    if (inferredPreferred) {
      inferredPreferredCategoryKeys.push(inferredPreferred);
    }
  }

  const textRefinement =
    (args.textRefinementRules ?? [])
      .filter((rule) => matchesTextRefinementSourceIds(rule, inputSourceIds))
      .find((rule) => matchesTextRefinementRule(rule, refinementText)) ?? null;

  const strongerCategoryKeys = new Set([...directCategoryKeys, ...inferredPreferredCategoryKeys]);

  for (const { sourceId, rule } of matchedRules) {
    if (rule.mode === "direct") {
      continue;
    }

    if (rule.fallbackCategoryKey) {
      const blockers =
        rule.blockedByCategoryKeys?.filter((key) => strongerCategoryKeys.has(key)) ?? [];
      if (blockers.length > 0) {
        blockedFallbacks.push({
          sourceId,
          categoryKey: rule.fallbackCategoryKey,
          blockedByCategoryKeys: blockers,
        });
      } else {
        fallbackCategoryKeys.push(rule.fallbackCategoryKey);
      }
    } else if (rule.mode === "fallback" && rule.categoryKey) {
      const blockers =
        rule.blockedByCategoryKeys?.filter((key) => strongerCategoryKeys.has(key)) ?? [];
      if (blockers.length > 0) {
        blockedFallbacks.push({
          sourceId,
          categoryKey: rule.categoryKey,
          blockedByCategoryKeys: blockers,
        });
      } else {
        fallbackCategoryKeys.push(rule.categoryKey);
      }
    }

    if (rule.mode === "mixed" || rule.mode === "branch-to-descendant") {
      mixedCandidates.push({
        sourceId,
        preferredCategoryKeys: rule.preferredCategoryKeys ?? [],
        fallbackCategoryKey: rule.fallbackCategoryKey ?? rule.categoryKey ?? null,
        requiresSupport: rule.requiresSupport ?? [],
      });
    }
  }

  const orderedCategoryKeys = unique([
    ...(textRefinement ? [textRefinement.categoryKey] : []),
    ...directCategoryKeys,
    ...inferredPreferredCategoryKeys,
    ...fallbackCategoryKeys,
  ]);

  return {
    inputSourceIds,
    ignoredSourceIds,
    unmatchedSourceIds,
    highRiskOverrideSourceIds,
    directCategoryKeys: unique(directCategoryKeys),
    inferredPreferredCategoryKeys: unique(inferredPreferredCategoryKeys),
    fallbackCategoryKeys: unique(fallbackCategoryKeys),
    blockedFallbacks,
    mixedCandidates,
    textRefinement: textRefinement
      ? {
          sourceId: textRefinement.sourceId ?? textRefinement.sourceIds?.[0] ?? null,
          categoryKey: textRefinement.categoryKey,
          reason: textRefinement.reason,
        }
      : null,
    orderedCategoryKeys,
  };
}

export function evaluateFoodCategoryPolicy(args: FoodCategoryPolicyEvaluationArgs): string[] {
  return evaluateFoodCategoryPolicyInternal(args).orderedCategoryKeys;
}

export function evaluateFoodCategoryPolicyDetails(
  args: FoodCategoryPolicyEvaluationArgs,
): FoodCategoryPolicyEvaluation {
  return evaluateFoodCategoryPolicyInternal(args);
}
